"use client";
import React, { useReducer, useEffect, useRef, useMemo } from "react";
import Badge from "@/components/primitives/badge";
import Avatar from "@/components/primitives/avatar";
import Icon from "@/components/primitives/icon";
import * as Collapsible from "@radix-ui/react-collapsible";
import GameComment from "../game/comment";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import CommentForm from "../game/comment-form";
import {
  GameDiscussionBettorModel,
  GameDiscussionCommentModel,
  GameDiscussionModel,
  Sport,
} from "common";
import NotFound from "../primitives/not-found";
import { useGameRoom } from "@/utils/providers/game-room";

type DiscussionAction =
  | {
      type: "add_comments";
      comments: GameDiscussionCommentModel[];
      bettors: GameDiscussionBettorModel[];
    }
  | { type: "set_initial_data"; data: GameDiscussionModel };

function discussionReducer(
  state: GameDiscussionModel,
  action: DiscussionAction
): GameDiscussionModel {
  switch (action.type) {
    case "set_initial_data":
      return { ...action.data };
    case "add_comments": {
      const { comments, bettors } = action;
      const updatedBettors = { ...state.bettors };
      bettors.forEach((bettor) => {
        updatedBettors[bettor.id] = bettor;
      });
      const updatedComments = { ...state.comments };
      comments.forEach((comment) => {
        if (comment.parent_id) {
          const parent = updatedComments[comment.parent_id];
          if (parent) {
            parent.replies = [...(parent.replies || []), comment];
          }
        } else {
          updatedComments[comment.id] = comment;
        }
      });

      const updatedCommentCount = state.total_comments + comments.length;

      return {
        ...state,
        bettors: updatedBettors,
        comments: updatedComments,
        total_comments: updatedCommentCount,
        total_bettors: Object.keys(updatedBettors).length,
      };
    }

    default:
      return state;
  }
}

export default function DiscussionBoard({
  sport,
  gameId,
  data: initialData,
  closed,
}: {
  sport: Sport;
  gameId: number;
  data: GameDiscussionModel;
  closed: boolean;
}) {
  const [open, setOpen] = React.useState(!closed);
  const [state, dispatch] = useReducer(discussionReducer, initialData);
  // const wsDiscussion = useWebSocketMessage("game_comment_added");
  const wsDiscussion = useGameRoom();
  const commentContainerRef = useRef<HTMLDivElement>(null);

  function scrollToBottom() {
    if (commentContainerRef.current) {
      commentContainerRef.current.scrollTop =
        commentContainerRef.current.scrollHeight -
        commentContainerRef.current.clientHeight;
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  const attachBettorsToComments = (comments: {
    [key: number]: GameDiscussionCommentModel;
  }): GameDiscussionCommentModel[] => {
    const enrichedComments: GameDiscussionCommentModel[] = [];

    Object.values(comments).forEach((comment) => {
      enrichedComments.push({
        ...comment,
        bettor: state.bettors[comment.bettor_id] || undefined,
        replies: comment.replies
          ? attachBettorsToComments(
              comment.replies.reduce((acc, reply) => {
                acc[reply.id] = reply;
                return acc;
              }, {} as { [key: number]: GameDiscussionCommentModel })
            )
          : [],
      });
    });

    return enrichedComments;
  };

  useEffect(() => {
    if (!wsDiscussion.gameData) return;
    console.log("discussion ws", wsDiscussion);
    if (wsDiscussion?.gameData.type === "game_comment_added") {
      const { comments, bettors } = wsDiscussion.gameData.data;

      dispatch({ type: "add_comments", comments, bettors });
    }
  }, [wsDiscussion.gameData, gameId]);

  const enrichedComments = useMemo(
    () => attachBettorsToComments(state.comments),
    [state.comments, state.bettors]
  );

  // const totalComments = useMemo(() => {

  // }, [state.comments]);

  return (
    <Collapsible.Root
      open={open}
      onOpenChange={setOpen}
      defaultOpen={!closed ? true : false}
      className="group overflow-hidden shadow-card"
    >
      <Collapsible.Trigger asChild>
        <button className="relative z-[1] h-[56px] w-full py-0 px-4 rounded-xl flex items-center flex-row gap-4 justify-between bg-secondary-300">
          <div className="flex flex-row gap-x-4 w-full justify-between items-center">
            <div className="flex flex-row gap-4 items-center whitespace-nowrap min-w-[280px]">
              {!closed ? (
                <img
                  src="/images/discussion/discussion-open.png"
                  alt=""
                  className="h-12 -ml-2 -mr-2"
                />
              ) : (
                <img
                  src="/images/discussion/discussion-closed.png"
                  alt=""
                  className="h-8"
                />
              )}

              <div className="hidden desktop-grid:block text-headline-bold uppercase text-surface-100">
                {!closed
                  ? "Join the Discussion"
                  : !closed && enrichedComments.length
                  ? "View the Discussion"
                  : ""}
              </div>
              <div className="flex flex-row gap-4 items-center w-full desktop-grid:justify-between pr-8">
                <Badge
                  color="white"
                  value={state.total_comments}
                  showZero={true}
                  icon={
                    state.total_comments > 0
                      ? { name: "flame", gradient: "flame" }
                      : undefined
                  }
                  label={`Comment${state.total_comments !== 1 ? "s" : ""}`}
                />
                <Avatar.Group
                  avatars={Object.values(state.bettors).map((b) => b.avatarUrl)}
                  value={state.total_bettors}
                  label="Bettors"
                  side="right"
                  dark
                />
              </div>
            </div>
            {open ? (
              <Icon name="caret-up" className="fill-white"></Icon>
            ) : (
              <Icon name="caret-down" className="fill-white"></Icon>
            )}
          </div>
        </button>
      </Collapsible.Trigger>
      <Collapsible.Content className="data-[state=open]:animate-unCollapse data-[state=closed]:animate-collapse bg-white relative z-0 rounded-bl-lg rounded-br-lg">
        <div className="absolute -top-8 bg-white w-full h-8"></div>
        <div className="overflow-hidden">
          <ScrollArea.Root>
            <ScrollArea.Viewport
              ref={commentContainerRef}
              className="w-full h-full"
            >
              <div className="min-h-[6.5rem] max-h-[26.5rem] p-5">
                {enrichedComments.length ? (
                  <>
                    {enrichedComments.map((comment) => (
                      <GameComment
                        sport={sport}
                        gameId={gameId}
                        data={comment}
                        bettors={state.bettors}
                        key={comment.id}
                        disabled={!open}
                      />
                    ))}
                  </>
                ) : (
                  open && (
                    <div className="pt-8">
                      <NotFound label="Be the first to comment" />
                    </div>
                  )
                )}
              </div>
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar
              className="flex select-none touch-none p-0.5 bg-light transition-colors duration-[160ms] ease-out hover:bg-blackA5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:h-2.5"
              orientation="vertical"
            >
              <ScrollArea.Thumb className="flex-1 bg-secondary rounded-[10px]" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        </div>

        {open && (
          <div className="bg-white p-6 flex flex-row gap-2 items-center rounded-br-lg rounded-bl-lg">
            <CommentForm sport={sport} gameId={gameId} disabled={!open} />
          </div>
        )}
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
